import Box from '@mui/material/Box';
import { useStacklineTheme } from '@stackline/ui';
import React, { useMemo } from 'react';
import useMarketShareHooks from 'src/components/AtlasV2Components/Hooks/useMarketShareRequestHook';
import useAtlasV2MultipleIndexHooks from 'src/components/AtlasV2Components/KeyMetricsComponents/AtlasV2MultipleIndexHooks';
import KeyMetricCards, {
  KeyMetricCardDefinition
} from 'src/components/BeaconV2Components/KeyMetricCards/KeyMetricCards';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { EnhancedCommonSummaryInfoWithLegend } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import { KEY_METRICS_CONTAINER_ADJUSTED_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { updateUrlQueryParams } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { buildSubtitleDisplayName } from 'src/utils/filters';
import { useHistory } from 'src/utils/Hooks';

const MULTIPLE_INDICES = ['retailPriceMarketShareV2'];
const MARKET_SHARE_INDICES = ['retailSalesMarketShareV2', 'unitsSoldMarketShareV2', 'retailPriceMarketShareV2'];

const titleOverrides = {
  promotions: {
    dataPointCount: 'Promotions'
  },
  advertising: {
    clicks: 'Paid Traffic'
  },
  sales: {
    retailSalesAvg: 'Sales per SKU',
    unitsSoldAvg: 'Units per SKU',
    purchaseRate: 'Conversion',
    retailSalesMarketShareV2: 'Share ($)',
    unitsSoldMarketShareV2: 'Share (UNITS)',
    stacklineSku: 'Products',
    retailPriceMarketShareV2: 'Price Index'
  },
  reviews: {
    stars: 'Rating'
  },
  buybox: {
    winPercentage: 'Buy Box'
  }
};

/**
 * Shows all the key metric summary cards for the BASIC (Restricted user, retailer with no traffic content etc  )Atlas home page
 */
const AtlasV2BasicKeyMetricsCards = () => {
  const theme = useStacklineTheme();
  const history = useHistory();
  const shouldUseMultiple = (fieldName) => MULTIPLE_INDICES.includes(fieldName);
  const multiIndexHook = useAtlasV2MultipleIndexHooks();
  const { requestBuilder, marketShareBuilder } = useMarketShareHooks();

  // handles some  hard coded logic - copied from Legacy Atlas
  const requestBuilderHandlers: {
    [key: string]: (request: AdvancedSearchRequestBuilder) => AdvancedSearchRequestBuilder;
  } = {
    searchTerm: (request) => request.addConditionRangeFilter('totalClicks', 1),
    reviewsRating: (request) => request.addConditionRangeFilter('reviewsRating', 0.01, 5),
    dataPointCount: (request) =>
      request
        .addConditionRangeFilter('retailSales', 0.01)
        .addSortField('Promotion Count', 'dataPointCount', 'sum', true),
    retailPriceMarketShareV2: (request) => requestBuilder(request),
    retailSalesMarketShareV2: (request) => requestBuilder(request),
    unitsSoldMarketShareV2: (request) => requestBuilder(request)
  };

  const cardDefinitions: KeyMetricCardDefinition[][] = useMemo(
    () =>
      [
        [
          ['sales', 'retailSales', 'sales', 'retailSales'],
          ['sales', 'retailSalesMarketShareV2', 'sales', 'retailSales'],
          ['sales', 'retailSalesAvg', 'sales', 'retailSales'],
          ['promotions', 'dataPointCount', 'prices', 'promotions']
        ],
        [
          ['sales', 'unitsSold', 'sales', 'unitsSold'],
          ['sales', 'unitsSoldMarketShareV2', 'sales', 'unitsSold'],
          ['sales', 'unitsSoldAvg', 'sales', 'unitsSold'],
          ['sales', 'stacklineSku', 'sales', 'retailSales']
        ],
        [
          ['sales', 'retailPrice', 'prices', 'retailPrice'],
          ['sales', 'retailPriceMarketShareV2', 'prices', 'retailPrice'],
          ['sales', 'contentScore', 'content', 'contentScore'],
          ['sales', 'reviewsRating', 'rating', 'rating']
        ]
      ].map((columnDefinitions) =>
        columnDefinitions.map(([indexName, fieldName, tab, subtab]) => ({
          indexName,
          fieldName,
          title: titleOverrides[indexName] ? titleOverrides[indexName][fieldName] : undefined,
          metricTypeDisplay: MULTIPLE_INDICES.includes(fieldName) ? METRICTYPE.MULTIPLE : null,
          shouldSliceByWeekRange: fieldName === 'dataPointCount',
          buildMetricSummaryTrendCardProps: (defaultProps) => {
            if (shouldUseMultiple(fieldName)) {
              return multiIndexHook(defaultProps);
            } else {
              return defaultProps;
            }
          },
          onClick: () => history.push(updateUrlQueryParams({ tab, subtab })),
          primaryAggregationBuilder: (aggregationBuilder) => {
            if (fieldName === 'reviewsRating') {
              aggregationBuilder.addConditionRangeFilter('reviewsRating', 0.01, 5);
            }

            if (fieldName === 'stacklineSku') {
              aggregationBuilder.addConditionRangeFilter('unitsSold', 4);
            }

            return aggregationBuilder;
          },

          secondaryAggregationBuilder: (aggregationBuilder) => {
            if (fieldName === 'reviewsRating') {
              aggregationBuilder.addConditionRangeFilter('reviewsRating', 0.01, 5);
            }

            if (fieldName === 'stacklineSku') {
              aggregationBuilder.addConditionRangeFilter('unitsSold', 4);
            }

            if (fieldName === 'retailSalesMarketShareV2') {
              aggregationBuilder
                .clearAggregationFields()
                .addAggregationField('Retail Sales', 'retailSales', 'sum', true);
            }

            return aggregationBuilder;
          },
          requestBuilder: requestBuilderHandlers[fieldName]
            ? (request) => {
                return requestBuilderHandlers[fieldName](request);
              }
            : undefined,

          marketShareBuilder: MARKET_SHARE_INDICES.includes(fieldName)
            ? (builder) => {
                return marketShareBuilder(builder);
              }
            : undefined
        }))
      ),
    [history]
  );

  return (
    <>
      <EnhancedCommonSummaryInfoWithLegend
        widget={{
          view: {
            buildSubTitle: ({ retailer, mainEntity, filters, categories, app }) =>
              mainEntity ? buildSubtitleDisplayName(retailer, mainEntity, filters, categories, app) : 'Summary',

            title: 'Key Metrics'
          }
        }}
      />
      <Box
        marginTop={theme.spacing.lg}
        marginBottom={theme.spacing.lg}
        sx={{
          transform: 'translateX(-16px)',
          width: KEY_METRICS_CONTAINER_ADJUSTED_WIDTH
        }}
      >
        <KeyMetricCards cardDefinitionsByColumn={cardDefinitions} />
      </Box>
    </>
  );
};

export default AtlasV2BasicKeyMetricsCards;
